<script setup>
/**
 * ONE PLAIN SENTENCE, AND AT MOST ONE FIX (spec §1 P-1.8), under a reply or a handover, in the
 * chat pane and on Activity.
 *
 * The sentence is the BFF's code (`authoring-web/src/why.ts`) in her language; the params are
 * her own words (a task title, a step, a saved question), so the line is `dir="auto"`. The fix
 * is whatever `whyAction` says it is for THIS viewer: a team member gets a sentence where an
 * administrator gets a button. Teaching and suggesting open dialogs the page owns, so this
 * only asks for them; the two fixes that are a request or a switch it carries out itself.
 */
import { computed, ref, watch } from 'vue';

import { useWasloI18n } from '../i18n.js';
import { wasloApi } from '../api/client.js';
import { whyAction, whyCopyKey } from '../helpers/why.js';

const props = defineProps({
  /** `{ code, params, fix, target }` from the BFF, or null. */
  why: { type: Object, default: null },
  accountId: { type: Number, default: null },
  isAdmin: { type: Boolean, default: true },
});

const emit = defineEmits(['teach', 'suggest', 'changed']);

const { t } = useWasloI18n();

const sentence = computed(() =>
  props.why ? t(whyCopyKey(props.why.code), props.why.params ?? {}) : ''
);

const action = computed(() =>
  whyAction(props.why, { accountId: props.accountId, isAdmin: props.isAdmin })
);

const label = computed(() => {
  const next = action.value;
  if (!next || next.kind === 'ask_owner') return '';
  if (next.kind === 'suggest') return t('WASLO.WHY.FIX.SUGGEST');
  return t(`WASLO.WHY.FIX.${String(props.why.fix).toUpperCase()}`);
});

const askOwner = computed(() =>
  action.value?.kind === 'ask_owner'
    ? t(`WASLO.WHY.ASK_OWNER.${String(action.value.fix).toUpperCase()}`)
    : ''
);

/** `idle` → `pending` → `sent` or `error`, for the two fixes this carries out itself. */
const state = ref('idle');
watch(
  () => props.why,
  () => {
    state.value = 'idle';
  }
);

const press = async () => {
  const next = action.value;
  if (!next || state.value === 'pending' || state.value === 'sent') return;
  if (next.kind === 'teach') {
    emit('teach');
    return;
  }
  if (next.kind === 'suggest') {
    emit('suggest');
    return;
  }
  if (props.accountId === null) return;
  state.value = 'pending';
  try {
    if (next.kind === 'ask') {
      await wasloApi.requestSetup(props.accountId, next.what);
      state.value = 'sent';
      return;
    }
    const config = await wasloApi.updateConfig(props.accountId, {
      aiEnabled: true,
    });
    state.value = 'idle';
    emit('changed', config);
  } catch {
    state.value = 'error';
  }
};

const ACTION_CLASS =
  'inline-flex items-center h-7 px-2.5 text-xs font-medium rounded-lg text-n-slate-12 bg-n-alpha-2 hover:bg-n-slate-4';
</script>

<template>
  <div
    v-if="why"
    class="flex flex-col gap-1"
    data-waslo-why
    :data-waslo-why-code="why.code"
  >
    <p
      class="mb-0 text-xs leading-5 text-n-slate-11"
      dir="auto"
      data-waslo-why-sentence
    >
      {{ sentence }}
    </p>
    <p
      v-if="askOwner"
      class="mb-0 text-xs leading-5 text-n-slate-10"
      data-waslo-why-ask-owner
    >
      {{ askOwner }}
    </p>
    <div v-else-if="action" class="flex flex-wrap items-center gap-2">
      <router-link
        v-if="action.kind === 'link'"
        :to="action.to"
        :class="ACTION_CLASS"
        data-waslo-why-fix
      >
        {{ label }}
      </router-link>
      <button
        v-else-if="state !== 'sent'"
        type="button"
        :class="[ACTION_CLASS, state === 'pending' ? 'opacity-60' : '']"
        :aria-disabled="state === 'pending'"
        data-waslo-why-fix
        @click="press"
      >
        {{ state === 'pending' ? t('WASLO.WHY.SENDING') : label }}
      </button>
      <span
        v-if="state === 'sent'"
        class="text-xs text-n-teal-11"
        data-waslo-why-sent
      >
        {{ t('WASLO.WHY.SENT') }}
      </span>
      <span
        v-if="state === 'error'"
        class="text-xs text-n-ruby-11"
        data-waslo-why-failed
      >
        {{ t('WASLO.WHY.FAILED') }}
      </span>
    </div>
  </div>
</template>
